import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { CreditCard, X, AlertCircle, CheckCircle, Clock, ExternalLink } from 'lucide-react';

interface Subscription {
  id: string;
  user_id: string;
  status: string;
  plan_type: string;
  price: number;
  start_date: string;
  end_date: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  auto_renew: boolean;
  user_email?: string;
  user_name?: string;
}

interface StripeSubscription {
  id: string;
  status: string;
  current_period_end: number;
  current_period_start: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  items: {
    data: Array<{
      price: {
        id: string;
        unit_amount: number;
        currency: string;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
  customer: any;
  default_payment_method: any;
}

export function SubscriptionsManagement() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [stripeDetails, setStripeDetails] = useState<StripeSubscription | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncingProductIds, setSyncingProductIds] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  async function fetchSubscriptions() {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const { data: allUsers } = await supabase.rpc('get_all_users_for_admin');
      const usersMap = new Map((allUsers || []).map((u: any) => [u.id, u]));

      const subscriptionsWithUsers = (data || []).map((subscription) => {
        const userData = usersMap.get(subscription.user_id) as any;
        return {
          ...subscription,
          user_email: userData?.email || 'Unknown',
          user_name: userData?.first_name && userData?.last_name
            ? `${userData.first_name} ${userData.last_name}`
            : userData?.email || 'Unknown',
        };
      });

      setSubscriptions(subscriptionsWithUsers);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStripeDetails(subscriptionId: string) {
    setLoadingDetails(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ subscriptionId }),
        }
      );

      const result = await response.json();

      if (response.status === 404 || result.error === 'not_found') {
        setStripeDetails(null);
        return;
      }

      if (!response.ok) throw new Error(result.error || 'Failed to fetch Stripe details');

      setStripeDetails(result.subscription);
    } catch (error) {
      console.error('Error fetching Stripe details:', error);
      alert('Impossible de récupérer les détails Stripe');
    } finally {
      setLoadingDetails(false);
    }
  }

  async function cancelSubscription(subscriptionId: string, immediately: boolean) {
    if (!confirm(
      immediately
        ? 'Annuler l\'abonnement immédiatement ? L\'utilisateur perdra l\'accès tout de suite.'
        : 'Annuler l\'abonnement à la fin de la période ? L\'utilisateur gardera l\'accès jusqu\'à la fin.'
    )) {
      return;
    }

    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-stripe-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ subscriptionId, immediately }),
        }
      );

      if (!response.ok) throw new Error('Failed to cancel subscription');

      const result = await response.json();
      alert(result.message);

      setSelectedSubscription(null);
      setStripeDetails(null);
      await fetchSubscriptions();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Erreur lors de l\'annulation de l\'abonnement');
    } finally {
      setActionLoading(false);
    }
  }

  async function syncStripeProductIds() {
    if (!confirm('Synchroniser les Product IDs Stripe pour tous les abonnements actifs ?')) {
      return;
    }

    setSyncingProductIds(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-stripe-product-ids`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to sync product IDs');

      const result = await response.json();
      alert(`Synchronisation terminée: ${result.updated}/${result.total} abonnement(s) mis à jour`);
      await fetchSubscriptions();
    } catch (error) {
      console.error('Error syncing product IDs:', error);
      alert('Erreur lors de la synchronisation des Product IDs');
    } finally {
      setSyncingProductIds(false);
    }
  }

  function getStatusBadge(status: string) {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Actif' },
      suspended: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Suspendu' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: X, label: 'Annulé' },
      trialing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock, label: 'Essai' },
      past_due: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertCircle, label: 'Impayé' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.suspended;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Chargement des abonnements...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des abonnements</h2>
        <div className="flex items-center gap-4">
          <button
            onClick={syncStripeProductIds}
            disabled={syncingProductIds}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {syncingProductIds ? 'Synchronisation...' : 'Sync Product IDs'}
          </button>
          <div className="text-sm text-gray-600">
            {subscriptions.length} abonnement{subscriptions.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Début</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin période</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renouvellement</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {subscriptions.map((subscription) => (
              <tr key={subscription.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="text-sm font-medium text-gray-900">{subscription.user_name}</div>
                  <div className="text-xs text-gray-500">{subscription.user_email}</div>
                  {subscription.stripe_customer_id && (
                    <div className="text-xs text-gray-400 mt-1">
                      Stripe: {subscription.stripe_customer_id.substring(0, 15)}...
                    </div>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900 capitalize">{subscription.plan_type}</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm font-medium text-gray-900">
                  {subscription.price.toFixed(2)} €
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(subscription.status)}
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {new Date(subscription.start_date).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {subscription.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString('fr-FR')
                    : subscription.end_date
                    ? new Date(subscription.end_date).toLocaleDateString('fr-FR')
                    : '-'}
                </td>
                <td className="px-4 py-4">
                  <span className={`text-xs ${subscription.auto_renew ? 'text-green-600' : 'text-gray-600'}`}>
                    {subscription.auto_renew ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <button
                    onClick={() => {
                      setSelectedSubscription(subscription);
                      if (subscription.stripe_subscription_id) {
                        fetchStripeDetails(subscription.stripe_subscription_id);
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Détails
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {subscriptions.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Aucun abonnement trouvé
        </div>
      )}

      {selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Détails de l'abonnement</h3>
              <button
                onClick={() => {
                  setSelectedSubscription(null);
                  setStripeDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Informations client</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nom:</span>
                    <span className="font-medium text-gray-900">{selectedSubscription.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">{selectedSubscription.user_email}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Détails de l'abonnement</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium text-gray-900 capitalize">{selectedSubscription.plan_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prix:</span>
                    <span className="font-medium text-gray-900">{selectedSubscription.price.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Statut:</span>
                    {getStatusBadge(selectedSubscription.status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Renouvellement auto:</span>
                    <span className={`font-medium ${selectedSubscription.auto_renew ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedSubscription.auto_renew ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedSubscription.stripe_subscription_id && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ExternalLink size={16} />
                    Informations Stripe
                  </h4>

                  {loadingDetails ? (
                    <div className="text-center py-4 text-gray-600">Chargement...</div>
                  ) : stripeDetails ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ID Abonnement:</span>
                        <span className="font-mono text-xs text-gray-900">{stripeDetails.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Statut Stripe:</span>
                        <span className="font-medium text-gray-900">{stripeDetails.status}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Période actuelle:</span>
                        <span className="text-gray-900">
                          {new Date(stripeDetails.current_period_start * 1000).toLocaleDateString('fr-FR')}
                          {' → '}
                          {new Date(stripeDetails.current_period_end * 1000).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      {stripeDetails.cancel_at_period_end && (
                        <div className="flex items-center gap-2 p-2 bg-yellow-100 rounded">
                          <AlertCircle size={16} className="text-yellow-700" />
                          <span className="text-yellow-700 text-xs">
                            Sera annulé à la fin de la période
                          </span>
                        </div>
                      )}
                      {stripeDetails.canceled_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Annulé le:</span>
                          <span className="text-gray-900">
                            {new Date(stripeDetails.canceled_at * 1000).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <AlertCircle size={16} className="text-yellow-600 flex-shrink-0" />
                      <div className="text-sm text-yellow-800">
                        Données indisponibles — cet abonnement appartient à l'ancien compte Stripe.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedSubscription.status === 'active' && selectedSubscription.stripe_subscription_id && (
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => cancelSubscription(selectedSubscription.stripe_subscription_id!, false)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                  >
                    Annuler à la fin
                  </button>
                  <button
                    onClick={() => cancelSubscription(selectedSubscription.stripe_subscription_id!, true)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Annuler immédiatement
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
